import {Feature} from '@hconnect/common/enums'
import {useUserContext} from '@hconnect/login'
import {useUpmPlant} from '@hconnect/upm'
import {useTranslation} from 'react-i18next'
import {Navigate} from 'react-router'

import {PermissionParsed, usePermissionsQuery} from '../../permissions'
import {checkPermission} from '../../permissions/helpers/checkPermission'
import {
  getUrl,
  MATERIALS_PERMISSION_TYPES,
  mainRoutes,
  PLANT_ROUTES,
  PLANT_SETUP_PERMISSION_TYPES,
  ROUTES,
  RouteName
} from '../../routing'
import {useUrlParam} from '../../routing/hooks'
import {PageSkeleton} from '../components/skeletons'
import {useLatestQuery, useSettingsFeature} from '../hooks'
import {Latest} from '../types/latest'
import {logger} from '../utils/logger'

const log = logger.context('DefaultPageRedirect')

export const getRouteToRedirect = (
  isUpmAssetsAndStoragesEnabled: boolean,
  plantCode: string,
  countryId: string,
  permissions: PermissionParsed[],
  latest: Latest
) => {
  const filteredMainRoutes = isUpmAssetsAndStoragesEnabled
    ? mainRoutes.filter(
        (route) =>
          route.path !== PLANT_ROUTES.STORAGES.path && route.path !== PLANT_ROUTES.ASSETS.path
      )
    : mainRoutes

  return filteredMainRoutes.find((route) => {
    const {permissionType, featureType} = route

    const permissionTypes =
      route.name === RouteName.PLANT_SETUP
        ? PLANT_SETUP_PERMISSION_TYPES
        : route.name === RouteName.MATERIALS_SETTINGS
          ? MATERIALS_PERMISSION_TYPES
          : [permissionType]
    const hasPermission = permissionTypes.some((permissionType) =>
      checkPermission(permissionType, plantCode, permissions, [countryId])
    )

    const hasFeature = !featureType || latest.available_features.includes(featureType as Feature)

    return hasPermission && hasFeature
  })
}

export const DefaultPageRedirect = () => {
  const {t} = useTranslation()

  const plantCode = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId: plantCode, shouldRaiseError: false})

  const {data: permissions} = usePermissionsQuery()
  const {data: latest} = useLatestQuery()
  const {loggedUser} = useUserContext()
  const isUpmAssetsAndStoragesEnabled = useSettingsFeature(Feature.UpmAssetsAndStorages)

  if (!loggedUser || !permissions || !latest) {
    return <PageSkeleton />
  }

  if (upmPlant === undefined) {
    const errorMessage = t('error.pageNotFoundTitle')
    log.extras({plantCode}).critical(errorMessage)
    return <Navigate to={getUrl(ROUTES.BASE.NOT_FOUND.path, {})} replace />
  }

  const routeToRedirect = getRouteToRedirect(
    isUpmAssetsAndStoragesEnabled,
    plantCode,
    upmPlant.country,
    permissions,
    latest
  )

  if (!routeToRedirect) {
    const errorMessage = t('error.accessDenied')
    log.extras({plantCode}).critical(errorMessage)
    return <Navigate to={getUrl(ROUTES.BASE.NOT_AUTHORIZED.path, {})} replace />
  }

  return <Navigate to={getUrl(routeToRedirect.path, {plantCode})} replace />
}
